import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user-context";
import { apiRequest, parseApiError } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";

const MAX_SCREENSHOT_SIZE = 960_000; // ~700KB raw image in base64

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function BugReportDialog({ open, onOpenChange }: BugReportDialogProps) {
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<{ dataUrl: string; filename: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const { toast } = useToast();
  const { user } = useUser();
  const [location] = useLocation();

  const displayName = user
    ? user.displayName || user.username || "Unknown"
    : "Unknown";

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setDescription("");
      setScreenshot(null);
      setDragOver(false);
      setSizeError(false);
    }
  }, [open]);

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (dataUrl.length > MAX_SCREENSHOT_SIZE) {
        setSizeError(true);
        return;
      }
      setSizeError(false);
      setScreenshot({ dataUrl, filename: file.name || `screenshot-${Date.now()}.png` });
    };
    reader.readAsDataURL(file);
  }, []);

  // Paste handler — only active while dialog is open
  useEffect(() => {
    if (!open) return;
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processFile(file);
          break;
        }
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [open, processFile]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/bug-reports", {
        description,
        pageUrl: location,
        userAgent: navigator.userAgent,
        submittedBy: displayName,
        ...(screenshot ? { screenshot } : {}),
      });
    },
    onSuccess: () => {
      toast({ title: "Bug report sent — thanks!" });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Failed to send bug report", description: parseApiError(err), variant: "destructive", severity: "error" });
    },
  });

  const canSubmit = description.trim().length >= 10 && !submitMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Report a Bug</DialogTitle>
          <DialogDescription>
            Describe what happened and paste a screenshot if helpful.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Description */}
          <div>
            <Textarea
              placeholder="Describe what happened... (min 10 characters)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="resize-none"
              autoFocus
            />
            {description.length > 0 && description.trim().length < 10 && (
              <p className="text-xs text-muted-foreground mt-1">
                {10 - description.trim().length} more characters needed
              </p>
            )}
          </div>

          {/* Screenshot drop zone / preview */}
          {screenshot ? (
            <div className="relative rounded-md border bg-muted/30 p-2">
              <button
                type="button"
                onClick={() => { setScreenshot(null); setSizeError(false); }}
                className="absolute top-1 right-1 rounded-full bg-background border p-0.5 hover:bg-muted transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <img
                src={screenshot.dataUrl}
                alt="Screenshot preview"
                className="max-h-40 rounded object-contain mx-auto"
              />
              <p className="text-xs text-muted-foreground text-center mt-1">{screenshot.filename}</p>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file) processFile(file);
              }}
              className={`rounded-md border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/40"
              }`}
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = () => {
                  const file = input.files?.[0];
                  if (file) processFile(file);
                };
                input.click();
              }}
            >
              {dragOver ? (
                <ImageIcon className="h-8 w-8 mx-auto mb-2 text-primary" />
              ) : (
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              )}
              <p className="text-sm text-muted-foreground">
                Paste, drag, or click to add a screenshot
              </p>
            </div>
          )}

          {sizeError && (
            <p className="text-xs text-destructive">
              Screenshot is too large. Try cropping or reducing resolution before pasting.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => submitMutation.mutate()} disabled={!canSubmit}>
            {submitMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
